/**
 * Cookie utility for sharing cookies across subdomains of caro.care
 */
import { DEFAULT_PORTAL_THEME } from '../theme/defaults';

// Function to extract the root domain from the current URL
const getRootDomain = () => {
  const hostname = window.location.hostname;
  
  // If we're on localhost for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return hostname;
  }
  
  // Extract the root domain (e.g., 'caro.care' from 'files.caro.care')
  const parts = hostname.split('.');
  if (parts.length > 2) {
    // Get the top two domain parts (domain.tld)
    return `.${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  }
  
  // If it's already a root domain (e.g., 'caro.care')
  return `.${hostname}`;
};

/**
 * Set a cookie that can be accessed across all caro.care subdomains
 * 
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiration (defaults to 365)
 */
export const setCrossDomainCookie = (name, value, days = 365) => {
  const rootDomain = getRootDomain();
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  
  // Set cookie for the root domain with the appropriate domain attribute
  document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/; domain=${rootDomain}; SameSite=Lax`;
};

/**
 * Get a cookie value by name
 * 
 * @param {string} name - Cookie name
 * @return {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }
  
  return null;
};

/**
 * Remove a cross-domain cookie
 * 
 * @param {string} name - Cookie name
 */
export const removeCrossDomainCookie = (name) => {
  const rootDomain = getRootDomain();
  
  // Remove the cookie by setting an expired date
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain}; SameSite=Lax`;
};

/**
 * Cookie keys used in the application
 */
export const COOKIE_KEYS = {
  THEME_MODE: 'caro_theme_mode',
  THEME_COLOR: 'caro_theme_color',
  THEME_LAYOUT: 'caro_theme_layout',
  LANGUAGE: 'caro_language',
  TYPOGRAPHY: 'caro_typography',
  // Accessibility cookies
  TEXT_SIZE: 'caro_text_size',
  CONTRAST_MODE: 'caro_contrast_mode',
  REDUCED_MOTION: 'caro_reduced_motion',
  // One-time marker of the plum → editorial default switch (T117-T119). Read by the index.html
  // preload script as well, so keep the literal name in step there.
  THEME_DEFAULT_MIGRATED: 'caro_theme_default_v2',
};

export const DEFAULT_COOKIE_VALUES = {
  [COOKIE_KEYS.THEME_MODE]: 'light',
  // Values: 'plum' | 'gold' | 'editorial' (brand themes, theme/brand-themes.js) or a colour
  // preset name. The default is the single constant of theme/defaults.js (AC-14).
  [COOKIE_KEYS.THEME_COLOR]: DEFAULT_PORTAL_THEME,
  [COOKIE_KEYS.THEME_LAYOUT]: 'default',
  [COOKIE_KEYS.LANGUAGE]: 'en',
  [COOKIE_KEYS.TYPOGRAPHY]: 'Montserrat, sans-serif',
  [COOKIE_KEYS.TEXT_SIZE]: 'medium',
  [COOKIE_KEYS.CONTRAST_MODE]: 'false',
  [COOKIE_KEYS.REDUCED_MOTION]: 'false',
};

/**
 * Get a theme preference from cookies or return the default value
 * 
 * @param {string} key - Cookie key from COOKIE_KEYS
 * @return {string} Value from cookie or default
 */
export const getThemePreference = (key) => {
  return getCookie(key) || DEFAULT_COOKIE_VALUES[key];
};

/**
 * One-time move to the Editorial default (spec 20260930-caro-editorial-theme, T117-T119).
 *
 * Until 2026-09-30 the default was 'plum', and a stored 'plum' cannot be told apart from a user
 * who simply never chose. So, once per browser (marker cookie, cross-domain like the theme
 * cookie): a missing or 'plum' theme cookie becomes 'editorial'; 'gold' and every other choice
 * are kept. After the marker is set this never touches the cookie again, so picking Plum in
 * /settings afterwards sticks. Idempotent; safe under StrictMode double-invocation.
 *
 * @return {boolean} true when the theme cookie was rewritten
 */
export const migratePortalThemeDefault = () => {
  try {
    if (getCookie(COOKIE_KEYS.THEME_DEFAULT_MIGRATED)) return false;
    const current = getCookie(COOKIE_KEYS.THEME_COLOR);
    const move = !current || current === 'plum';
    if (move) setCrossDomainCookie(COOKIE_KEYS.THEME_COLOR, 'editorial');
    setCrossDomainCookie(COOKIE_KEYS.THEME_DEFAULT_MIGRATED, '1');
    return move;
  } catch {
    return false;
  }
};